import * as React from 'react'
import { Helmet } from 'react-helmet'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { PermissionNames, SecurityRole, PermissionState } from '@getgreenline/homi-shared'
import { Routes } from '../../../../constants/Routes'
import { AppView, AppMainView } from '../../../../components/AppView'
import AppHeader from '../../../../components/AppView/Header'
import { Divider, Skeleton, Table, Alert, message } from 'antd'
import { PermissionsDrawer } from './PermissionsDrawer'
import { CreateSecurityRoleModal } from './CreateSecurityRoleModal'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { HelpLinkMenu } from '../../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import { ADMINISTRATOR } from '../AddEmployee'
import { TooltipWithIcon } from '../../../../components/TooltipWithIcon'
import { BLAZE_COMPANY } from '../../../../constants/general'
import { HelpLinks } from '../../../../constants/ExternalLinks'

interface Props {
  currentCompanyStore: CurrentCompanyStore
  currentUserStore: CurrentUserStore
}

interface State {
  showAddSecurityRole: boolean
  selectedSecurityRoleForDetails?: SecurityRole
  selectedSecurityRoleForPermissions?: SecurityRole
}

interface IDependency {
  permission: PermissionNames
  shouldBe: boolean
}

interface IDependencyCondition {
  nextStatus: boolean
  dependencies: IDependency[]
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class DashboardSecurityRoles extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      showAddSecurityRole: false,
    }
  }

  componentDidMount() {
    this.props.currentCompanyStore!.getSecurityRoles()
  }

  get forbiddenPermissions(): PermissionNames[] {
    return [
      PermissionNames.CAN_EDIT_BRANDS,
      PermissionNames.CAN_VIEW_BRANDS,
      PermissionNames.CAN_VIEW_LICENSED_PRODUCERS,
      PermissionNames.CAN_EDIT_LICENSED_PRODUCERS,
    ]
  }

  get dependencyPermissions(): Partial<{ [key in PermissionNames]: IDependencyCondition }> {
    return {
      [PermissionNames.CAN_EDIT_BROADCASTS]: {
        nextStatus: true,
        dependencies: [
          {
            permission: PermissionNames.CAN_VIEW_BROADCASTS,
            shouldBe: true,
          },
        ],
      },
      [PermissionNames.CAN_VIEW_BROADCASTS]: {
        nextStatus: false,
        dependencies: [
          {
            permission: PermissionNames.CAN_EDIT_BROADCASTS,
            shouldBe: false,
          },
        ],
      },
      [PermissionNames.CAN_BULK_EDIT_PRODUCTS]: {
        nextStatus: false,
        dependencies: [
          {
            permission: PermissionNames.CAN_BULK_EDIT_PRICE_OVERRIDES,
            shouldBe: false,
          },
          {
            permission: PermissionNames.CAN_BULK_EDIT_DETAILS_OVERRIDES,
            shouldBe: false,
          },
        ],
      },
      [PermissionNames.CAN_BULK_EDIT_PRICE_OVERRIDES]: {
        nextStatus: true,
        dependencies: [
          {
            permission: PermissionNames.CAN_BULK_EDIT_PRODUCTS,
            shouldBe: true,
          },
        ],
      },
      [PermissionNames.CAN_BULK_EDIT_DETAILS_OVERRIDES]: {
        nextStatus: true,
        dependencies: [
          {
            permission: PermissionNames.CAN_BULK_EDIT_PRODUCTS,
            shouldBe: true,
          },
        ],
      },
    }
  }

  checkPermissionDependencies(permissionToCheck: PermissionState, securityRole: SecurityRole) {
    const dependencyCondition = this.dependencyPermissions[permissionToCheck.permission.name]
    if (dependencyCondition) {
      const conditionMatch = !permissionToCheck.state === dependencyCondition.nextStatus

      conditionMatch &&
        dependencyCondition.dependencies.forEach((dependency) => {
          const permissionDependency = securityRole.permissions.find(
            (permissionState) => permissionState.permission.name === dependency.permission,
          )
          if (permissionDependency) permissionDependency.state = dependency.shouldBe
        })
    }
  }
  async update(securityRole: SecurityRole, toggledPermissionId: number) {
    try {
      const permission = securityRole.permissions.find(
        (permissionState) => permissionState.permission.id === toggledPermissionId,
      )!
      this.checkPermissionDependencies(permission, securityRole)
      // if statement should be removed to fully release brand/lp feature
      if (!this.forbiddenPermissions.includes(permission.permission.name)) {
        permission.state = !permission.state
        await this.props.currentCompanyStore!.updateSecurityRole(securityRole)
        this.props.currentUserStore.getMyPermissions()
        message.success('Updated', 1)
      }
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error')
    }
  }

  render() {
    const company = this.props.currentCompanyStore.company!
    const securityRoles = this.props.currentCompanyStore.securityRoles
    const {
      showAddSecurityRole,
      selectedSecurityRoleForDetails,
      selectedSecurityRoleForPermissions,
    } = this.state

    const columns = [
      {
        title: 'Security role',
        key: 'securityRole',
        render: (securityRole: SecurityRole) => securityRole.name,
      },
      {
        title: 'Description',
        key: 'description',
        render: (securityRole: SecurityRole) => securityRole.description,
      },
      {
        title: 'Options',
        key: 'options',
        render: (securityRole: SecurityRole) => (
          <span>
            {securityRole.name === ADMINISTRATOR && !this.props.currentUserStore.isAdmin ? (
              <span>
                <span className='text-muted'>Edit name</span>
                <Divider type='vertical' />
                <span className='text-muted'>Edit permissions</span>
              </span>
            ) : (
              <span>
                <a onClick={() => this.setState({ selectedSecurityRoleForDetails: securityRole })}>
                  Edit name
                </a>
                <Divider type='vertical' />
                <a
                  onClick={() =>
                    this.setState({ selectedSecurityRoleForPermissions: securityRole })
                  }
                >
                  Edit permissions
                </a>
                {securityRole.name === ADMINISTRATOR && this.props.currentUserStore.isAdmin && (
                  <TooltipWithIcon
                    tooltipIconClassName={'ml-2 fa fa-warning'}
                    tooltipContent={
                      <span>
                        You can edit because you are a {BLAZE_COMPANY} admin - be careful!
                      </span>
                    }
                  />
                )}
              </span>
            )}
          </span>
        ),
      },
    ]

    return (
      <AppView column>
        <Helmet>
          <title>Employees | Security Roles</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <AppHeader title='Security roles'>
          <span className='spaced-button-wrapper'>
            <HelpLinkMenu
              helpLinkMenus={{
                href: HelpLinks.EMPLOYEES,
                title: 'Adding employees',
              }}
            />

            <BlazeButton
              buttonProps={{
                icon: 'eye',
                href: Routes.DASHBOARD_EMPLOYEES_EMPLOYEES(company.id),
              }}
              titleProps={{
                title: 'View employees',
              }}
            />

            <BlazeButton
              buttonProps={{
                type: 'primary',
                icon: 'plus',
                onClick: () => this.setState({ showAddSecurityRole: true }),
              }}
              titleProps={{
                title: 'Create security role',
              }}
            />
          </span>
        </AppHeader>
        <AppMainView>
          <div className='container'>
            <Alert
              type='info'
              showIcon
              message='Security roles help you control feature access for employees'
              description={
                <>
                  Security roles are important as you increase your employee count.
                  <br />
                  Once you have edited/created your security roles, assign them to your employees at
                  all or specific locations.
                </>
              }
            />
            <br />
            {securityRoles ? (
              <div className='table-responsive'>
                <Table
                  rowKey={(role) => role.id.toString()}
                  columns={columns}
                  dataSource={securityRoles}
                  pagination={false}
                />
              </div>
            ) : (
              <Skeleton active />
            )}
          </div>

          {showAddSecurityRole && (
            <CreateSecurityRoleModal
              onClose={() => this.setState({ showAddSecurityRole: false })}
              onSubmit={() => this.setState({ showAddSecurityRole: false })}
            />
          )}

          {selectedSecurityRoleForDetails && (
            <CreateSecurityRoleModal
              securityRole={selectedSecurityRoleForDetails}
              onClose={() => this.setState({ selectedSecurityRoleForDetails: undefined })}
              onSubmit={() => this.setState({ selectedSecurityRoleForDetails: undefined })}
            />
          )}

          {selectedSecurityRoleForPermissions && (
            <PermissionsDrawer
              securityRole={selectedSecurityRoleForPermissions}
              onUpdate={(permissionId: number) =>
                this.update(selectedSecurityRoleForPermissions, permissionId)
              }
              onClose={() => this.setState({ selectedSecurityRoleForPermissions: undefined })}
            />
          )}
        </AppMainView>
      </AppView>
    )
  }
}
